import { useEffect, useId, useMemo, useRef, useState, type DragEvent } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useApplyFleetImportMutation,
  useDiscardFleetImportMutation,
  usePreviewFleetImportMutation,
  type FleetImport,
  type FleetImportResult,
} from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { matchesAccept } from '@shared/lib/pickFiles';
import { AnalysisBar } from '@shared/ui/AnalysisBar';
import { Button } from '@shared/ui/Button';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
} from '@shared/ui/Drawer';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { fleetLeading, fleetType, formatCount, typo } from '@entities/fleet';
import {
  ChangedSection,
  Counters,
  InfoSections,
  NewSection,
  ResultTiles,
  type ChangedModel,
  type NewModel,
} from './PreviewSections';

/** Книга ЭММ — .xlsx (openpyxl в doc-processor); .xls 97–2003 не читается. */
const ACCEPT = '.xlsx,.xlsm';
/** Как `MAX_BOOK_BYTES` в `routes/fleet.rs`. */
const MAX_MB = 32;
const OLE_MAGIC = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];

/** Книга старого формата .xls (OLE) — openpyxl её не читает; тот же отказ у загрузки ПРЦ. */
export async function isOldExcel(file: File): Promise<boolean> {
  try {
    const head = new Uint8Array(await file.slice(0, OLE_MAGIC.length).arrayBuffer());
    return head.length === OLE_MAGIC.length && OLE_MAGIC.every((b, i) => head[i] === b);
  } catch {
    return false;
  }
}

type Stage =
  | { kind: 'pick' }
  | { kind: 'parsing'; fileName: string; startedAt: number }
  | { kind: 'preview'; data: FleetImport; selected: ReadonlySet<string> }
  | { kind: 'done'; fileName: string; result: FleetImportResult; prcChanged: number };

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(3)};
  padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Titles = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Eyebrow = styled.p`
  margin: 0;
  font-size: ${fleetType.micro};
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Title = styled.h2`
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
  font-size: ${fleetType.title};
  font-weight: 600;
  line-height: ${fleetLeading.tight};
  text-wrap: balance;
  overflow-wrap: anywhere;

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

const Lead = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: ${fleetType.base};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
  max-width: 68ch;

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

const Box = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Alert = styled.div<{ $tone: 'danger' | 'success' }>`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme, $tone }) => theme.colors.status[$tone]}59;
  border-left: 3px solid ${({ theme, $tone }) => theme.colors.status[$tone]};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $tone }) => theme.colors.status[$tone]}14;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  text-wrap: pretty;
`;

const Status = styled.p`
  flex: 1 1 220px;
  min-width: 0;
  margin: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.tight};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

function elapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function changedOf(data: FleetImport): ChangedModel[] {
  return data.summary.models.filter((m): m is ChangedModel => m.status === 'changed');
}

function newOf(data: FleetImport): NewModel[] {
  return data.summary.models.filter((m): m is NewModel => m.status === 'new');
}

/** По умолчанию применяется всё, что меняет справочник. */
function selectable(data: FleetImport): string[] {
  return data.summary.models.filter((m) => m.status !== 'unchanged').map((m) => m.name);
}

/**
 * Загрузка книги «Калькуляция ЭММ» в справочник техники (ТЗ п. 6.1.3):
 * файл → разбор и предпросмотр «что изменится» → «Применить» отмеченное или
 * «Отменить». Пока не нажато «Применить», в справочник ничего не пишется.
 */
export function FleetImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return open ? <ImportBody onClose={onClose} /> : null;
}

function ImportBody({ onClose }: { onClose: () => void }) {
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'pick' });
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const pending = useRef<{ abort: () => void } | null>(null);
  const alive = useRef(true);

  const [preview] = usePreviewFleetImportMutation();
  const [apply, { isLoading: applying }] = useApplyFleetImportMutation();
  const [discard] = useDiscardFleetImportMutation();

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      pending.current?.abort();
    };
  }, []);

  // Часы разбора: книга читается до минуты, человеку важно видеть, что идёт.
  useEffect(() => {
    if (stage.kind !== 'parsing') return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [stage.kind]);

  // Новый шаг — фокус на заголовок, чтобы скринридер прочёл, что изменилось.
  useEffect(() => {
    if (stage.kind === 'preview' || stage.kind === 'done') {
      titleRef.current?.focus({ preventScroll: true });
    }
  }, [stage.kind]);

  async function onFile(file: File) {
    setError(null);
    if (file.size === 0) {
      setError('Файл пустой — выберите книгу ЭММ.');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`Файл больше ${MAX_MB} МБ — сервер такую книгу не примет.`);
      return;
    }
    if (await isOldExcel(file)) {
      if (alive.current) {
        setError(
          'Это книга старого формата .xls (Excel 97–2003). Откройте её в Excel и сохраните как .xlsx: Файл → Сохранить как → Книга Excel.',
        );
      }
      return;
    }
    if (!alive.current) return;
    const form = new FormData();
    form.append('file', file, file.name);
    setNow(Date.now());
    setStage({ kind: 'parsing', fileName: file.name, startedAt: Date.now() });
    const req = preview(form);
    pending.current = req;
    try {
      const res = await req.unwrap();
      if (!alive.current) return;
      setStage({ kind: 'preview', data: res.import, selected: new Set(selectable(res.import)) });
    } catch (err) {
      if (!alive.current) return;
      setStage({ kind: 'pick' });
      setError(apiErrorText(err, 'Не удалось разобрать книгу.'));
    } finally {
      if (pending.current === req) pending.current = null;
    }
  }

  /** Бросили файл не того формата — зона молча отбросила бы его, объясняем. */
  function onDropCapture(e: DragEvent<HTMLDivElement>) {
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 0 && !files.some((f) => matchesAccept(f.name, ACCEPT))) {
      setError(`«${files[0]?.name ?? 'файл'}» — нужна книга Excel (.xlsx) «Калькуляция ЭММ».`);
    }
  }

  function close() {
    if (stage.kind === 'parsing') pending.current?.abort();
    // Предпросмотр, который не применили, отменяем — в справочник он не попадёт.
    if (stage.kind === 'preview') void discard(stage.data.id);
    onClose();
  }

  function setSelected(update: (prev: ReadonlySet<string>) => ReadonlySet<string>) {
    setStage((s) => (s.kind === 'preview' ? { ...s, selected: update(s.selected) } : s));
  }

  function toggle(name: string, value: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (value) next.add(name);
      else next.delete(name);
      return next;
    });
  }

  function toggleMany(names: readonly string[], value: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const n of names) {
        if (value) next.add(n);
        else next.delete(n);
      }
      return next;
    });
  }

  async function onApply() {
    if (stage.kind !== 'preview') return;
    const all = selectable(stage.data);
    const chosen = all.filter((n) => stage.selected.has(n));
    if (chosen.length === 0) return;
    setError(null);
    try {
      // Отмечено всё — без списка: backend применит книгу целиком.
      const res = await apply({
        id: stage.data.id,
        accept: chosen.length === all.length ? undefined : chosen,
      }).unwrap();
      const prcChanged = changedOf(stage.data).filter(
        (m) => m.prc_fields_changed && stage.selected.has(m.name),
      ).length;
      setStage({ kind: 'done', fileName: stage.data.filename, result: res.result, prcChanged });
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось применить загрузку.'));
    }
  }

  const changed = useMemo(() => (stage.kind === 'preview' ? changedOf(stage.data) : []), [stage]);
  const added = useMemo(() => (stage.kind === 'preview' ? newOf(stage.data) : []), [stage]);
  const unchanged = useMemo(
    () =>
      stage.kind === 'preview'
        ? stage.data.summary.models.filter((m) => m.status === 'unchanged').map((m) => m.name)
        : [],
    [stage],
  );

  const total = changed.length + added.length;
  const chosen =
    stage.kind === 'preview'
      ? [...changed, ...added].filter((m) => stage.selected.has(m.name)).length
      : 0;

  const title =
    stage.kind === 'preview'
      ? `Предпросмотр: ${stage.data.filename}`
      : stage.kind === 'done'
        ? 'Справочник обновлён'
        : 'Загрузка книги ЭММ';

  return (
    <Drawer open onClose={close} labelledBy={titleId} size="lg" initialFocusRef={titleRef}>
      <DrawerHeader>
        <HeaderRow>
          <Titles>
            <Eyebrow>Справочник техники · импорт из Excel</Eyebrow>
            <Title id={titleId} ref={titleRef} tabIndex={-1}>
              {title}
            </Title>
          </Titles>
          <DrawerCloseButton onClick={close} />
        </HeaderRow>
      </DrawerHeader>

      <DrawerBody>
        {error && (
          <Alert $tone="danger" role="alert">
            {error}
          </Alert>
        )}

        {stage.kind === 'pick' && (
          <>
            <Lead>
              {typo(
                'Книга «Калькуляция ЭММ», лист «Справочник (паспорт техники)». Программа сравнит её со справочником и покажет, что изменится: новые модели, изменённые поля «было → стало», предупреждения. Пока вы не нажмёте «Применить», в справочник ничего не запишется.',
              )}
            </Lead>
            <Box onDropCapture={onDropCapture}>
              <FileDropZone
                accept={ACCEPT}
                primary="Перетащите книгу ЭММ (.xlsx)"
                hint={`или нажмите, чтобы выбрать файл — до ${MAX_MB} МБ`}
                onFile={(f) => void onFile(f)}
              />
            </Box>
          </>
        )}

        {stage.kind === 'parsing' && (
          <Box aria-live="polite">
            <AnalysisBar
              title="Разбираю книгу — обычно до минуты"
              hint={`${stage.fileName} · прошло ${elapsed(now - stage.startedAt)}. Не закрывайте окно — предпросмотр появится здесь.`}
            />
          </Box>
        )}

        {stage.kind === 'preview' && (
          <>
            <Lead>
              <strong>В справочник ещё ничего не записано.</strong>{' '}
              {typo(
                total > 0
                  ? 'Снимите отметки с моделей, которые не нужно трогать, и нажмите «Применить».'
                  : 'Книга совпадает со справочником — применять нечего.',
              )}
            </Lead>
            <Counters summary={stage.data.summary} />
            {changed.length > 0 && (
              <ChangedSection
                models={changed}
                selected={stage.selected}
                onToggle={toggle}
                onToggleAll={(v) =>
                  toggleMany(
                    changed.map((m) => m.name),
                    v,
                  )
                }
              />
            )}
            {added.length > 0 && (
              <NewSection
                models={added}
                selected={stage.selected}
                onToggle={toggle}
                onToggleAll={(v) =>
                  toggleMany(
                    added.map((m) => m.name),
                    v,
                  )
                }
              />
            )}
            <InfoSections summary={stage.data.summary} unchanged={unchanged} />
          </>
        )}

        {stage.kind === 'done' && (
          <>
            <Alert $tone="success" role="status">
              {typo(
                `Книга «${stage.fileName}» применена: создано ${formatCount(stage.result.created)}, обновлено ${formatCount(stage.result.updated)}, без изменений ${formatCount(stage.result.unchanged)}, новых категорий ${formatCount(stage.result.categories_created)}.`,
              )}
            </Alert>
            <ResultTiles result={stage.result} />
            {stage.prcChanged > 0 && (
              <Lead>
                {typo(
                  `У ${formatCount(stage.prcChanged)} ${pluralizeRu(stage.prcChanged, ['модели', 'моделей', 'моделей'])} изменились поля ПРЦ — они помечены «ПРЦ устарела» и попадут в список на пересчёт.`,
                )}
              </Lead>
            )}
          </>
        )}
      </DrawerBody>

      <DrawerFooter>
        {stage.kind === 'preview' ? (
          <>
            <Status role="status">
              {total > 0
                ? `Применится ${formatCount(chosen)} из ${formatCount(total)} ${pluralizeRu(total, ['модели', 'моделей', 'моделей'])}`
                : 'Изменений нет'}
            </Status>
            <Buttons>
              <Button type="button" $variant="ghost" onClick={close} disabled={applying}>
                {total > 0 ? 'Отменить загрузку' : 'Закрыть'}
              </Button>
              {total > 0 && (
                <Button
                  type="button"
                  $variant="primary"
                  disabled={applying || chosen === 0}
                  title={chosen === 0 ? 'Отметьте хотя бы одну модель' : undefined}
                  onClick={() => void onApply()}
                >
                  {applying ? 'Применяю…' : `Применить (${formatCount(chosen)})`}
                </Button>
              )}
            </Buttons>
          </>
        ) : stage.kind === 'done' ? (
          <>
            <Status />
            <Button type="button" $variant="primary" onClick={onClose}>
              Готово
            </Button>
          </>
        ) : (
          <>
            <Status />
            <Button type="button" $variant="ghost" onClick={close}>
              Отмена
            </Button>
          </>
        )}
      </DrawerFooter>
    </Drawer>
  );
}
